import React, { useEffect, useMemo, useState } from 'react';

const ChatHistory = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general_chat');

  useEffect(() => {
    const url = `https://laravelapi.bidit.co.il/admin/chat-history?t=${Date.now()}`;
    fetch(url, { cache: 'no-store' })
      .then(res => res.json())
      .then(data => {
        setMessages(Array.isArray(data) ? [...data].reverse() : []); // מהחדש לישן
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const looksLikeTenderPrompt = message => {
    const value = String(message || '');
    return (
      value.includes('אתה מאפיין עבודות מקצועי של Bidit') ||
      value.includes('אתה מסכם אפיון עבודה עבור Bidit') ||
      value.includes('recommendedProfession') ||
      value.includes('recommendedDurationHours')
    );
  };

  const parseTenderJson = message => {
    let value = String(message || '').trim();
    if (!value) return null;

    // GPT may wrap the JSON in ```json ... ``` even when asked not to.
    value = value
      .replace(/^```(?:json)?\s*/i, '')
      .replace(/\s*```$/i, '')
      .trim();

    const firstBrace = value.indexOf('{');
    const lastBrace = value.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace > firstBrace) {
      value = value.slice(firstBrace, lastBrace + 1);
    }

    try {
      return JSON.parse(value);
    } catch (_) {
      return null;
    }
  };

  const looksLikeTenderJson = message => {
    const parsed = parseTenderJson(message);
    return Boolean(
      parsed &&
      (
        parsed?.status === 'question' ||
        parsed?.status === 'ready' ||
        Object.prototype.hasOwnProperty.call(parsed, 'recommendedProfession') ||
        Object.prototype.hasOwnProperty.call(parsed, 'recommendedDurationHours') ||
        Object.prototype.hasOwnProperty.call(parsed, 'description')
      )
    );
  };

  const cleanAssistantMessage = msg => {
    const display = String(msg.display_message || '').trim();
    const raw = String(msg.message || '').trim();

    // אם השרת כבר סיפק טקסט נקי - משתמשים בו.
    if (display && display !== raw && !looksLikeTenderPrompt(display) && !looksLikeTenderJson(display)) {
      return display;
    }

    if (looksLikeTenderJson(raw)) {
      const parsed = parseTenderJson(raw);
      if (parsed?.status === 'question' && parsed.question) return parsed.question;
      if (parsed?.status === 'ready') return '';
      if (parsed?.title || parsed?.description) {
        return [parsed.title, parsed.description].filter(Boolean).join('\n');
      }
      return '';
    }

    return looksLikeTenderPrompt(raw) ? '' : (display || raw);
  };

  const normalizedMessages = useMemo(
    () => messages.map(msg => {
      const sessionId = String(msg.session_id || '');
      const isTender =
        msg.source === 'tender_assistant' ||
        sessionId.startsWith('tender-assistant-') ||
        looksLikeTenderPrompt(msg.message) ||
        looksLikeTenderJson(msg.message);

      return {
        ...msg,
        source: isTender ? 'tender_assistant' : (msg.source || 'general_chat'),
        display_message: isTender ? cleanAssistantMessage(msg) : (msg.display_message || msg.message),
      };
    }),
    [messages]
  );

  const filteredMessages = useMemo(
    () => normalizedMessages.filter(msg => {
      if (msg.source !== activeTab) return false;
      if (activeTab === 'tender_assistant') {
        return Boolean(String(msg.display_message || '').trim());
      }
      // הגנה נוספת מפני Prompts/JSON ישנים שזלגו ללוג הרגיל.
      // הודעת bot במבנה JSON שייכת לסייען, גם אם נוצרה לפני שהוסף source.
      return (
        !looksLikeTenderPrompt(msg.message) &&
        !looksLikeTenderJson(msg.message) &&
        !String(msg.session_id || '').startsWith('tender-assistant-')
      );
    }),
    [normalizedMessages, activeTab]
  );

  const assistantConversations = useMemo(() => {
    if (activeTab !== 'tender_assistant') return [];

    const grouped = new Map();
    filteredMessages.forEach(msg => {
      const key = msg.session_id || 'ללא-מזהה';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push(msg);
    });

    return Array.from(grouped.entries()).map(([sessionId, items]) => ({
      sessionId,
      items: [...items].reverse(), // בתוך שיחה: מהישן לחדש
      latestTimestamp: items[0]?.timestamp || '',
    }));
  }, [filteredMessages, activeTab]);

  const tabButtonStyle = isActive => ({
    border: '1px solid #d7e2ea',
    borderRadius: 10,
    padding: '9px 16px',
    fontFamily: 'Assistant, sans-serif',
    fontWeight: 700,
    cursor: 'pointer',
    background: isActive ? '#1976D2' : '#ffffff',
    color: isActive ? '#ffffff' : '#31546a',
  });

  const renderMessage = (msg, index) => (
    <div key={`${msg.session_id || 'session'}-${msg.timestamp || ''}-${index}`} style={{ marginTop: 10 }}>
      <div
        style={{
          background: msg.sender === 'user' ? '#eef6ff' : '#fff6d8',
          borderRadius: 10,
          padding: '10px 12px',
          textAlign: 'right',
          whiteSpace: 'pre-wrap',
          lineHeight: 1.55,
        }}
      >
        <strong>{msg.sender === 'user' ? '👤 משתמש:' : '🤖 בידאיט:'}</strong>{' '}
        {msg.display_message}
      </div>
    </div>
  );

  return (
    <div style={{ padding: 16, maxWidth: 980, margin: '0 auto', direction: 'rtl', fontFamily: 'Assistant, sans-serif' }}>
      <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 16, textAlign: 'center' }}>📋 היסטוריית שיחות</h1>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
        <button type="button" onClick={() => setActiveTab('general_chat')} style={tabButtonStyle(activeTab === 'general_chat')}>
          צ'אט רגיל
        </button>
        <button type="button" onClick={() => setActiveTab('tender_assistant')} style={tabButtonStyle(activeTab === 'tender_assistant')}>
          סייען מכרזים
        </button>
      </div>

      {loading ? (
        <p>טוען שיחות...</p>
      ) : activeTab === 'general_chat' ? (
        <div>
          {filteredMessages.length === 0 && <p style={{ textAlign: 'center' }}>אין שיחות להצגה.</p>}
          {filteredMessages.map((msg, index) => (
            <div key={index} style={{ border: '1px solid #e2e8ee', borderRadius: 12, padding: 14, marginBottom: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ fontSize: 13, color: '#73808a' }}>⏱️ {msg.timestamp} | 🧾 מזהה שיחה: {msg.session_id}</div>
              {renderMessage(msg, index)}
            </div>
          ))}
        </div>
      ) : (
        <div>
          {assistantConversations.length === 0 && <p style={{ textAlign: 'center' }}>עדיין אין שיחות של סייען המכרזים.</p>}
          {assistantConversations.map(conversation => (
            <div key={conversation.sessionId} style={{ border: '1px solid #dce7ef', borderRadius: 14, padding: 16, marginBottom: 16, background: '#ffffff', boxShadow: '0 3px 12px rgba(25,76,110,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', fontSize: 13, color: '#73808a' }}>
                <span>🧾 שיחה: {conversation.sessionId}</span>
                <span>⏱️ {conversation.latestTimestamp}</span>
              </div>
              <div style={{ marginTop: 8 }}>
                {conversation.items.map((msg, index) => renderMessage(msg, index))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatHistory;
